import { privacy } from "@redotech/schema-privacy";
import * as z from "zod";

export const MarketplaceFulfillmentDelayCriteriaSchema = z
  .object({
    delayDays: z.number().int().min(1).meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });
export const MarketplaceFulfillmentDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((fulfillmentDay) => {
    try {
      Temporal.PlainDate.from(fulfillmentDay);
      return true;
    } catch {
      return false;
    }
  });
export type MarketplaceFulfillmentDelayCriteria = z.infer<
  typeof MarketplaceFulfillmentDelayCriteriaSchema
>;

export function getFulfillmentNotificationReleaseHour(
  configuredReleaseHour: number | null | undefined,
): number {
  return typeof configuredReleaseHour === "number" &&
    Number.isInteger(configuredReleaseHour) &&
    configuredReleaseHour >= 0 &&
    configuredReleaseHour <= 23
    ? configuredReleaseHour
    : 9;
}

export function formatFulfillmentNotificationReleaseTime(
  configuredReleaseHour: number | null | undefined,
): string {
  const releaseHour = getFulfillmentNotificationReleaseHour(
    configuredReleaseHour,
  );
  const twelveHour = releaseHour % 12 || 12;
  const period = releaseHour < 12 ? "AM" : "PM";
  return `${twelveHour}:00 ${period}`;
}

export function getFulfillmentDateForDay({
  fulfillmentDay,
  releaseHour,
  timeZone,
}: {
  fulfillmentDay: string;
  releaseHour: number;
  timeZone: string;
}): Date {
  const fulfillmentDate = Temporal.PlainDate.from(
    fulfillmentDay,
  ).toZonedDateTime({ timeZone, plainTime: { hour: releaseHour } });

  return new Date(fulfillmentDate.epochMilliseconds);
}

export function getFulfillmentDayForDate({
  fulfillmentDate,
  timeZone,
}: {
  fulfillmentDate: Date;
  timeZone: string;
}): string {
  return Temporal.Instant.fromEpochMilliseconds(fulfillmentDate.getTime())
    .toZonedDateTimeISO(timeZone)
    .toPlainDate()
    .toString();
}

export function normalizeFulfillmentDate({
  fulfillmentDate,
  fulfillmentDay,
  releaseHour,
  timeZone,
}: {
  fulfillmentDate?: Date | null;
  fulfillmentDay?: string | null;
  releaseHour: number;
  timeZone: string;
}): Date | null | undefined {
  if (fulfillmentDay === undefined && fulfillmentDate === undefined) {
    return undefined;
  }
  if (fulfillmentDay === null) {
    return null;
  }

  const normalizedFulfillmentDay = fulfillmentDay
    ? fulfillmentDay
    : fulfillmentDate
      ? getFulfillmentDayForDate({ fulfillmentDate, timeZone })
      : null;
  if (!normalizedFulfillmentDay) {
    return null;
  }

  return getFulfillmentDateForDay({
    fulfillmentDay: normalizedFulfillmentDay,
    releaseHour,
    timeZone,
  });
}

export interface MarketplaceFulfillmentSettings {
  notifyMarketplace?: boolean | null;
  fulfillmentDate?: Date | null;
}

export type ResolvedMarketplaceFulfillmentSettings =
  | { notifyMarketplace: false; fulfillmentDate?: never }
  | { notifyMarketplace: true; fulfillmentDate?: Date };

export function resolveMarketplaceFulfillmentSettings({
  notifyMarketplace,
  fulfillmentDate,
}: MarketplaceFulfillmentSettings): ResolvedMarketplaceFulfillmentSettings {
  if (notifyMarketplace === false) {
    return { notifyMarketplace: false };
  }
  return {
    notifyMarketplace: true,
    fulfillmentDate: fulfillmentDate ?? undefined,
  };
}

export function getFulfillmentDateForDelay({
  criteria,
  now,
  releaseHour,
  timeZone,
}: {
  criteria: MarketplaceFulfillmentDelayCriteria;
  now: Date;
  releaseHour: number;
  timeZone: string;
}): Date {
  const fulfillmentDay = Temporal.Instant.fromEpochMilliseconds(now.getTime())
    .toZonedDateTimeISO(timeZone)
    .toPlainDate()
    .add({ days: criteria.delayDays })
    .toString();

  return getFulfillmentDateForDay({ fulfillmentDay, releaseHour, timeZone });
}
